/* eslint-disable local/ensure-wac-token-usage */
import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useState
} from "react";

import { css } from "@compiled/react";
import { injectIntl, useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import Button from "@atlaskit/button";
import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import {
    ufoEventConfirmationPageTemplateSelectionBack,
    ufoEventConfirmationPageTemplateSelectionContinue
} from "@/components/signup/utils";
import { wacToken } from "@/libraries/design/foundations";
import {
    TRACKING_EVENT_ACTION_SUBJECT_IDS,
    USER_SEARCH_PARAMS
} from "@/templates/ondemand/signup/confirmation/confirmation-page/src/views/user-seg-bxp-ui/constants.ts";
import {
    experiments,
    getExperiment,
    getExperimentWithExposures
} from "@/utils/experiment";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

import {
    JiraTemplateChoice,
    useGupsStatus,
    useJiraTemplateChoice
} from "../../hooks";
import { useConfirmationPage } from "../../hooks/use-confirmation-page";
import { useScreenManager } from "../../hooks/use-screen-manager";
import {
    USER_SEGMENTATION_TEAM_TYPE_FINANCE,
    USER_SEGMENTATION_TEAM_TYPE_HR,
    USER_SEGMENTATION_TEAM_TYPE_LEGAL,
    USER_SEGMENTATION_TEAM_TYPE_MARKETING,
    USER_SEGMENTATION_TEAM_TYPE_OPERATIONS,
    UserSegmentationTeamTypes
} from "../../models/user-segmentation";
import { getCurrentUrlWithParams } from "../../utils/utils";
import { JiraOnboardingContainer } from "../../views/jira/jira-onboarding-container/jira-onboarding-container";

import Body from "./components/body";
import { getTeamTypeTemplateOptions } from "./team-type-template-map";
import { TemplateOption, TemplateOptionMetadata } from "./template-definitions";

// Visual update styles for cfind4831ProductOverlayModalVisualUpdates gate
const titleVisualUpdateCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    h1: {
        fontFamily: wacToken("font.charlie.display"),
        fontWeight: 700,
        fontSize: "32px",
        lineHeight: "38px"
    }
});

const subheaderVisualUpdateWrapperCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    p: {
        font: token("font.body"),
        color: token("color.text.subtle"),
        margin: "8px 0 32px"
    }
});

const buttonVisualUpdateCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    button: {
        paddingBlock: "6px !important",
        paddingInline: "12px !important",
        margin: "0 !important"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    span: {
        fontFamily: wacToken("font.charlie.text") + " !important",
        fontSize: "14px !important",
        lineHeight: "1.5rem !important",
        fontWeight: `${token("font.weight.medium")} !important`,
        textAlign: "center",
        margin: "0 !important"
    }
});

const backButtonVisualUpdateCss = css({
    outline: `1px solid ${token("color.border")}`,
    outlineOffset: "-1px",
    borderRadius: "3px",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    button: {
        paddingBlock: "6px !important",
        paddingInline: "12px !important",
        margin: "0 !important"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    span: {
        fontFamily: wacToken("font.charlie.text") + " !important",
        fontSize: "14px !important",
        lineHeight: "1.5rem !important",
        fontWeight: `${token("font.weight.medium")} !important`,
        textAlign: "center",
        margin: "0 !important"
    }
});

const buttonWrapperVisualUpdateStyle = css({
    gap: token("space.100"),
    padding: 0,
    margin: 0,
    marginBlockStart: token("space.300"),
    width: "100%"
});

const buttonWrapperStyleDefault = css({
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    margin: "0 auto",
    padding: "36px 20px 20px 20px",
    border: "none",
    gap: token("space.250"),
    width: "372px",
    [media.below.sm]: {
        position: "fixed",
        insetInlineStart: 0,
        insetBlockEnd: "0",
        backgroundColor: "#FFF",
        padding: "14px 18px",
        width: "100%",
        zIndex: 1000,
        boxShadow: "-1px 0px 2px 0px #00000040"
    }
});

const buttonCssOverrideDefault = css({
    borderRadius: "40px !important",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    padding: "0 24px !important",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["& span"]: {
        fontFamily: "var(--bxp-charlie-text-font-family)",
        fontSize: "16px"
    },
    [media.above.sm]: {
        width: "min-content !important"
    }
});

const backButtonOverrideDefault = css({
    border: "2px solid #101214 !important",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["& span"]: {
        [media.below.sm]: {
            position: "relative",
            insetBlockStart: "-2px"
        }
    },
    [media.above.sm]: {
        lineHeight: "33px !important"
    }
});

const shouldSkipScreenForJiraFfl = (
    teamType: UserSegmentationTeamTypes
): boolean => {
    const fflTeamTypes: UserSegmentationTeamTypes[] = [
        USER_SEGMENTATION_TEAM_TYPE_OPERATIONS,
        USER_SEGMENTATION_TEAM_TYPE_MARKETING,
        USER_SEGMENTATION_TEAM_TYPE_HR,
        USER_SEGMENTATION_TEAM_TYPE_LEGAL,
        USER_SEGMENTATION_TEAM_TYPE_FINANCE
    ];

    if (!fflTeamTypes.includes(teamType)) {
        return false;
    }

    // Check for Jira FFL CGS key parameter to prevent overexposure
    // Only expose FFL experiment to users who have the FFL key in URL with a non-empty value
    const url = new URL(window.location.href);
    const cgsKeyValue = url.searchParams.get("cgsKey");
    const hasValidCgsKey = cgsKeyValue && cgsKeyValue.trim() !== "";

    if (!hasValidCgsKey) {
        return false;
    }

    const jiraFflExperiment = getExperiment(
        experiments.cfind6215FrictionlessFirstLookJiraV2
    );

    return jiraFflExperiment.type === "enrolled" && jiraFflExperiment.value;
};

const shouldSkipScreenForMadLib = (
    teamType: UserSegmentationTeamTypes
): boolean => {
    const madlibTeamTypes: UserSegmentationTeamTypes[] = [
        USER_SEGMENTATION_TEAM_TYPE_OPERATIONS,
        USER_SEGMENTATION_TEAM_TYPE_MARKETING,
        USER_SEGMENTATION_TEAM_TYPE_HR,
        USER_SEGMENTATION_TEAM_TYPE_LEGAL,
        USER_SEGMENTATION_TEAM_TYPE_FINANCE
    ];

    if (!madlibTeamTypes.includes(teamType)) {
        return false;
    }

    const madLibExperiment = getExperimentWithExposures(
        experiments.jiraOnboardingAiMadlibV2
    );

    return madLibExperiment.type === "enrolled" && madLibExperiment.value;
};

const getMessages = (
    formatMessage: (parameters: {
        id: string;
        defaultMessage: string;
    }) => string
) => ({
    header: formatMessage({
        id: "TransparentTemplateChoice.header.latest",
        defaultMessage: "Select a template to get started"
    }),
    subheader: formatMessage({
        id: "TransparentTemplateChoice.subheader.latest",
        defaultMessage: "Your choice won't limit what you can do in Jira"
    }),
    back: formatMessage({
        id: "TransparentTemplateChoice.backButton",
        defaultMessage: "Back"
    }),
    continue: formatMessage({
        id: "TransparentTemplateChoice.continueButton",
        defaultMessage: "Continue"
    }),
    transparentTemplateV2subHeader: formatMessage({
        id: "TransparentTemplateV2.subheader",
        defaultMessage:
            "You can always change this later. Selecting a template won’t limit what you can do."
    })
});

export const TemplateChoiceViewBase = ({
    contextSelectedTeamTypeState
}: {
    contextSelectedTeamTypeState: UserSegmentationTeamTypes;
}) => {
    const intl = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { addTemplateCallback } = useGupsStatus();

    //set these on submit
    const {
        setTemplateChoice,
        setTemplateChoiceId,
        setTemplateChoiceName,
        goBackToUserSegmentation,
        setTemplateMetadata
    } = useJiraTemplateChoice();

    const { prevScreen, nextScreen, prevScreenExists, skipScreen } =
        useScreenManager();

    const {
        userSegmentationContext: { selectedTeamType: rawSelectedTeamType }
    } = useConfirmationPage();

    const selectedTeamType = contextSelectedTeamTypeState;

    const doesPreviousScreenExist = prevScreenExists && prevScreenExists();

    const removeUrlParameter = useCallback((url: string, paramKey: string) => {
        const r = new URL(url);
        r.searchParams.delete(paramKey);
        return r.href;
    }, []);

    //set these on select
    const [selectedTemplate, setSelectedTemplate] =
        useState<JiraTemplateChoice>();
    const [selectedTemplateId, setSelectedTemplateId] = useState<string>();
    const [selectedCardTitle, setSelectedCardTitle] = useState<string>();
    const [selectedTemplateMetadata, setSelectedTemplateMetadata] =
        useState<TemplateOptionMetadata>();

    // get template choices based on team type
    const teamTemplateChoices: TemplateOption[] = useMemo(
        () => getTeamTypeTemplateOptions(selectedTeamType, intl.formatMessage),
        [intl.formatMessage, selectedTeamType]
    );

    useLayoutEffect(() => {
        // Ensure team type param is set in the URL before experiment exposure.
        // This allows for proper enrollment into the experiment for existing Jira users
        // that do not set their team type on the previous screen and
        // instead, it is resolved from a fetch request to their ID profile.
        const url = new URL(window.location.href);
        if (!rawSelectedTeamType) {
            return;
        }
        if (!url.searchParams.get("team_type")) {
            url.searchParams.set("team_type", rawSelectedTeamType);
            history.replaceState(null, "", url.toString());
        }

        if (shouldSkipScreenForMadLib(rawSelectedTeamType)) {
            skipScreen();
        }

        if (shouldSkipScreenForJiraFfl(rawSelectedTeamType)) {
            skipScreen();
        }
    }, [skipScreen, rawSelectedTeamType]);

    //set selected template to first template in array
    useEffect(() => {
        if (teamTemplateChoices) {
            setSelectedTemplateId(teamTemplateChoices[0]?.option.templateId);
            setSelectedTemplate(teamTemplateChoices[0]?.option.templateType);
            setSelectedTemplateMetadata(teamTemplateChoices[0]?.metadata);
            setSelectedCardTitle(teamTemplateChoices[0]?.option.title);
        }
    }, [teamTemplateChoices]);

    const handleBack = useCallback(() => {
        try {
            ufoEventConfirmationPageTemplateSelectionBack.start();
            createAnalyticsEvent({
                eventType: "ui",
                action: "clicked",
                actionSubject: "button",
                actionSubjectId:
                    TRACKING_EVENT_ACTION_SUBJECT_IDS.templateChoiceBack,
                attributes: {
                    atlasTicket: "ATLAS-69623"
                }
            }).fire();
            const updatedUrl = removeUrlParameter(
                window.location.href,
                USER_SEARCH_PARAMS.teamType
            );
            window.history.replaceState({}, "", updatedUrl);
            goBackToUserSegmentation();
            prevScreen();
            ufoEventConfirmationPageTemplateSelectionBack.success();
        } catch (e) {
            ufoEventConfirmationPageTemplateSelectionBack.failure({
                error: e
            });
        }
    }, [
        createAnalyticsEvent,
        goBackToUserSegmentation,
        prevScreen,
        removeUrlParameter
    ]);

    const handleContinue = useCallback(async () => {
        try {
            ufoEventConfirmationPageTemplateSelectionContinue.start();
            ufoEventConfirmationPageTemplateSelectionContinue.setCustomData({
                teamType: selectedTeamType,
                template: selectedTemplate,
                templateId: selectedTemplateId
            });
            createAnalyticsEvent({
                eventType: "ui",
                action: "clicked",
                actionSubject: "button",
                actionSubjectId:
                    TRACKING_EVENT_ACTION_SUBJECT_IDS.templateChoiceStartNow,
                attributes: {
                    atlasTicket: "ATLAS-69623",
                    template: selectedTemplate,
                    teamType: selectedTeamType,
                    templateId: selectedTemplateId
                }
            }).fire();

            // add templateChoice to query param to maintain selection on refresh
            const updatedUrl = getCurrentUrlWithParams({
                templateId: selectedTemplateId || "",
                templateChoice: selectedTemplate || "",
                jtbd: selectedTemplateMetadata?.jtbd || ""
            });
            window.history.replaceState({}, "", updatedUrl);

            if (selectedTemplateId) {
                await addTemplateCallback(selectedTemplateId);
            }

            setTemplateMetadata(selectedTemplateMetadata);
            setTemplateChoice(selectedTemplate);
            setTemplateChoiceId(selectedTemplateId);
            setTemplateChoiceName(selectedCardTitle);

            nextScreen();
            ufoEventConfirmationPageTemplateSelectionContinue.success();
        } catch (e) {
            ufoEventConfirmationPageTemplateSelectionContinue.failure({
                error: e
            });
        }
    }, [
        addTemplateCallback,
        createAnalyticsEvent,
        nextScreen,
        selectedCardTitle,
        selectedTeamType,
        selectedTemplate,
        selectedTemplateId,
        selectedTemplateMetadata,
        setTemplateChoice,
        setTemplateChoiceId,
        setTemplateChoiceName,
        setTemplateMetadata
    ]);

    const messages = useMemo(
        () => getMessages(intl.formatMessage),
        [intl.formatMessage]
    );

    const modalVisualUpdates = checkFeatureFlagGate(
        featureFlags.cfind4831ProductOverlayModalVisualUpdates
    );

    return (
        <div
            css={[
                modalVisualUpdates && titleVisualUpdateCss,
                modalVisualUpdates && subheaderVisualUpdateWrapperCss
            ]}
        >
            <JiraOnboardingContainer
                header={messages.header}
                subheader={messages.transparentTemplateV2subHeader}
                hideFooterOnMobile={true}
            >
                <Body
                    setSelectedTemplate={setSelectedTemplate}
                    setSelectedTemplateId={setSelectedTemplateId}
                    setSelectedCardTitle={setSelectedCardTitle}
                    setSelectedTemplateMetadata={setSelectedTemplateMetadata}
                    selectedCardTitle={selectedCardTitle}
                    cards={teamTemplateChoices}
                    teamType={selectedTeamType}
                />
                <div
                    css={[
                        buttonWrapperStyleDefault,
                        modalVisualUpdates && buttonWrapperVisualUpdateStyle
                    ]}
                >
                    {doesPreviousScreenExist && (
                        <div
                            css={[
                                modalVisualUpdates && backButtonVisualUpdateCss
                            ]}
                        >
                            <Button
                                shouldFitContainer
                                appearance="subtle"
                                css={[
                                    !modalVisualUpdates &&
                                        buttonCssOverrideDefault,
                                    !modalVisualUpdates &&
                                        backButtonOverrideDefault
                                ]}
                                onClick={handleBack}
                            >
                                {messages.back}
                            </Button>
                        </div>
                    )}
                    <div css={[modalVisualUpdates && buttonVisualUpdateCss]}>
                        <Button
                            css={[
                                !modalVisualUpdates && buttonCssOverrideDefault
                            ]}
                            shouldFitContainer
                            appearance="primary"
                            onClick={handleContinue}
                        >
                            {messages.continue}
                        </Button>
                    </div>
                </div>
            </JiraOnboardingContainer>
        </div>
    );
};

export const TemplateChoiceView = injectIntl(() => {
    //screen analytics event here
    const {
        userSegmentationContext: {
            selectedTeamType:
                contextSelectedTeamTypeState = "software-development"
        }
    } = useConfirmationPage();

    return (
        <TemplateChoiceViewBase
            contextSelectedTeamTypeState={contextSelectedTeamTypeState}
        />
    );
});
